import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCw, ExternalLink } from 'lucide-react';
import {
  studentBillingService,
  type StudentSubscriptionCredit,
  type RefundRequestRow,
  type StudentLessonPackage,
} from '@/services/studentBilling';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';
import { formatMoney } from '@/utils/money';

const StudentSubscriptionsPage: React.FC = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subs, setSubs] = useState<StudentSubscriptionCredit[]>([]);
  const [refunds, setRefunds] = useState<RefundRequestRow[]>([]);
  const [packages, setPackages] = useState<StudentLessonPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [checkoutPending, setCheckoutPending] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPackagesError(null);
    try {
      const [credits, refundRows] = await Promise.all([
        studentBillingService.getLessonCredits(),
        studentBillingService.getRefundRequests().catch(() => [] as RefundRequestRow[]),
      ]);
      setSubs(credits);
      setRefunds(refundRows);
    } catch {
      toast({ title: 'Error', description: 'Failed to load subscriptions', variant: 'destructive' });
    }

    try {
      setPackages(await studentBillingService.listLessonPackages());
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Failed to load packages';
      setPackages([]);
      setPackagesError(msg);
      toast({ title: 'Packages unavailable', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const returning = searchParams.get('checkout') === 'return';
    const packageId = searchParams.get('packageId');
    if (!returning || !packageId) {
      return;
    }

    setCheckoutPending(packageId);
    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      attempts += 1;
      try {
        const status = await studentBillingService.getPaymentStatus(packageId);
        if (status.status === 'confirmed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setCheckoutPending(null);
          setSearchParams({});
          toast({
            title: 'Subscription active',
            description: status.message ?? 'Your lesson credits are ready. You can book lessons now.',
          });
          await load();
          return;
        }
        if (attempts >= maxAttempts) {
          if (pollRef.current) clearInterval(pollRef.current);
          setCheckoutPending(null);
          setSearchParams({});
          toast({
            title: 'Still processing',
            description:
              'Payment may still be processing. Refresh this page in a minute or contact your teacher.',
            variant: 'destructive',
          });
          await load();
        }
      } catch {
        if (attempts >= maxAttempts) {
          if (pollRef.current) clearInterval(pollRef.current);
          setCheckoutPending(null);
        }
      }
    };

    void poll();
    pollRef.current = setInterval(() => void poll(), 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [searchParams, setSearchParams, toast, load]);

  const startCheckout = async (packageId: string) => {
    setBusy(`checkout-${packageId}`);
    try {
      const returnUrl = `${window.location.origin}/billing/subscriptions?checkout=return&packageId=${encodeURIComponent(packageId)}`;
      const { checkoutUrl } = await studentBillingService.startPackageCheckout(packageId, returnUrl);
      window.location.href = checkoutUrl;
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Could not start checkout';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const cancelSub = async (id: string) => {
    setBusy(`cancel-${id}`);
    try {
      await studentBillingService.cancelSubscription(id);
      toast({
        title: 'Cancelled',
        description: 'Renewal cancelled. No further charges after this period. Refunds apply only if eligible.',
      });
      await load();
    } catch {
      toast({ title: 'Error', description: 'Could not cancel subscription', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const requestRefund = async (id: string) => {
    setBusy(`refund-${id}`);
    try {
      const res = await studentBillingService.requestSubscriptionRefund(
        id,
        'Requested from student dashboard'
      );
      toast({ title: 'Refund requested', description: res.message });
      await load();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data
          ? String((err.response.data as { error?: string }).error)
          : 'Could not submit refund request';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const subscribeCard = (
    <PromoCard>
      <PromoCardHeader>
        <PromoCardTitle>Subscribe to a package</PromoCardTitle>
        <PromoCardDesc>
          Choose a package from your teacher&apos;s catalog. Checkout is hosted by Lemon Squeezy. After
          payment, credits appear here and you can book lessons.
        </PromoCardDesc>
      </PromoCardHeader>
      <PromoCardBody className="space-y-3">
        {packagesError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {packagesError}
          </p>
        )}
        {!packagesError && packages.length === 0 ? (
          <p className="promo-empty text-sm">
            No active subscription packages in your school yet. Your teacher must create a package and
            set it <strong>Active</strong> (billing is linked automatically when they save).
          </p>
        ) : (
          packages.map((p) => (
            <div key={p.id} className="pkg-row">
              <div>
                <div className="pn">{p.name}</div>
                <div className="pd">
                  {p.teacherDisplayName} · {formatMoney(p.priceCents, p.currencyCode)} / period ·{' '}
                  {p.lessonsPerPeriod} lessons · {p.lessonDurationMinutes} min
                </div>
                {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}
                {!p.canSubscribe && p.subscribeBlockedReason && (
                  <p className="text-xs text-amber-700 mt-1">{p.subscribeBlockedReason}</p>
                )}
              </div>
              <Button
                size="sm"
                disabled={!p.canSubscribe || busy === `checkout-${p.id}`}
                title={p.subscribeBlockedReason ?? undefined}
                onClick={() => void startCheckout(p.id)}
              >
                <ExternalLink size={14} className="mr-1" />
                Subscribe
              </Button>
            </div>
          ))
        )}
      </PromoCardBody>
    </PromoCard>
  );

  return (
    <div className="space-y-4">
      {checkoutPending && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900">
          <p className="font-semibold">Confirming your payment…</p>
          <p className="mt-1 text-violet-800">
            You returned from secure checkout. Waiting for Lemon Squeezy to confirm and grant lesson
            credits (usually a few seconds).
          </p>
        </div>
      )}

      {!loading && subs.length === 0 && subscribeCard}

      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle>Subscriptions &amp; credits</PromoCardTitle>
          <PromoCardDesc>
            Full refund within 14 days if you have not booked any lesson in this billing period. You can
            always cancel renewal; refunds are only when eligible — see{' '}
            <a
              href="https://arrangelly.com/refund-policy"
              target="_blank"
              rel="noreferrer"
              className="text-violet-700 underline"
            >
              Refund Policy
            </a>
            .
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="flex justify-end mb-3">
            <button type="button" className="pill-btn" onClick={() => void load()} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {loading ? (
            <p className="promo-empty">Loading…</p>
          ) : subs.length === 0 ? (
            <p className="promo-empty">No lesson subscriptions yet.</p>
          ) : (
            <div className="space-y-3">
              {subs.map((s) => (
                <div key={s.subscriptionId} className="pkg-row">
                  <div>
                    <div className="pn">
                      {s.packageName ?? s.teacherDisplayName ?? 'Teacher'}
                    </div>
                    <div className="pd">
                      {s.status} · {s.lessonCreditsBalance} credits left ·{' '}
                      {s.quantityLessonsPerPeriod} per period
                      {s.teacherDisplayName && s.packageName ? ` · ${s.teacherDisplayName}` : ''}
                    </div>
                    {!s.canRequestRefund && s.refundIneligibleReason && (
                      <p className="text-xs text-slate-500 mt-1">{s.refundIneligibleReason}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {s.canRequestRefund && (
                      <button
                        type="button"
                        className="pill-btn primary"
                        disabled={busy === `refund-${s.subscriptionId}`}
                        onClick={() => void requestRefund(s.subscriptionId)}
                      >
                        Request refund
                      </button>
                    )}
                    {s.status === 'active' && !s.cancelAtPeriodEnd && (
                      <button
                        type="button"
                        className="pill-btn"
                        disabled={busy === `cancel-${s.subscriptionId}`}
                        onClick={() => void cancelSub(s.subscriptionId)}
                      >
                        Cancel renewal
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {refunds.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-bold text-slate-800 mb-2">Refund requests</h3>
              <ul className="space-y-2 text-xs text-slate-600">
                {refunds.map((r) => (
                  <li key={r.id}>
                    {r.status} · {new Date(r.createdAt).toLocaleDateString()}
                    {r.subscriptionId ? ` · sub ${r.subscriptionId.slice(0, 8)}…` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PromoCardBody>
      </PromoCard>

      {subs.length > 0 && subscribeCard}


      <p className="text-xs text-slate-500 text-center">
        <Link to="/billing" className="text-violet-700 hover:underline">
          ← Back to billing
        </Link>
      </p>
    </div>
  );
};

export default StudentSubscriptionsPage;
